import { GameTab } from '../../engine/tabs';
import { showToast } from '../../engine/toast';
import { playClick } from '../../engine/click-sound';
import {
  DecorationKind,
  fetchMyColony,
  fetchWorldMap,
  ResourceType,
  WorldMapPoi,
} from './worldmap-api';
import { fetchJoinableWorlds } from './world-api';
import { openSendArmyModal, targetHasOwnerTitle, targetOwnerName } from './send-army-modal';
import { titleBadgeHtml } from '../../engine/title-badge';
import {
  clampPanOffset,
  clampZoom,
  computeZoomedPan,
  DEFAULT_VISIBLE_WIDTH_FRACTION,
  DEFAULT_ZOOM,
  craterLocationIndexFor,
  CRATER_LOCATIONS,
  scatteredCraterArtVariantFor,
  colonyMapArtFor,
  drawCell,
  drawColonyLabel,
  drawTerrainBackground,
  drawIsoMultiCellSpriteUpright,
  drawIsoCellSprite,
  DUNE_HEIGHT_CELLS,
  DUNE_LOCATIONS,
  DUNE_WIDTH_CELLS,
  GRID_HEIGHT,
  GRID_WIDTH,
  isoGridToProjected,
  sectorsFor,
  revealedSectors,
  SECTOR_COLS,
  SECTOR_ROWS,
  isoProjectedToGridCell,
  isoProjectedToGridContinuous,
  isoToScreen,
  isoTileSizePx,
  ISO_HEIGHT_TO_WIDTH_RATIO,
  mountainArtVariantFor,
  mountainLocationIndexFor,
  MOUNTAIN_LOCATIONS,
  MAX_ZOOM,
  MinimapRect,
  PanOffset,
  readThemeColors,
  resolveCellVisual,
} from './worldmap-renderer';
import { t, type Translations } from '../../engine/i18n/i18n';
import { dismissHomeNotice, isHomeNoticePending } from '../../engine/home-notice';
import { isReduceMotionEnabled } from '../../engine/reduce-motion';
import { mountBoostHud } from '../../engine/boost-hud';
import { placePopover, type Box } from './popover-placement';
import {
  buildClouds,
  CLOUD_SPRITE_COUNT,
  emptyPlacement,
  emptyView,
  placeCloud,
} from './cloud-layer';
import {
  gridPositionToImagePixel,
  MARS_BAND_BOTTOM,
  MARS_BAND_TOP,
  MARS_IMAGE_HEIGHT,
  MARS_IMAGE_URL,
  MARS_IMAGE_URL_HIRES,
  marsScale,
  shouldLoadHiResMap,
  MARS_IMAGE_WIDTH,
} from './mars-texture';

/** Shared glossary names, except He-3 which the map spells out as "Helium-3". */
const RESOURCE_KEYS: Record<ResourceType, keyof Translations> = {
  CRIMSON_DUST: 'term.resource.CRIMSON_DUST',
  REGOLITH: 'term.resource.REGOLITH',
  WATER_ICE: 'term.resource.WATER_ICE',
  HE3: 'worldmap.resource.HE3',
  COMPOSITE: 'term.resource.COMPOSITE',
};

function resourceLabel(type: ResourceType | null): string {
  if (!type) return t('worldmap.resource.unknown');
  const key = RESOURCE_KEYS[type];
  return key ? t(key) : type;
}

const DECORATION_KEYS: Record<DecorationKind, keyof Translations> = {
  MOUNTAIN: 'worldmap.decoration.MOUNTAIN',
  CRATER: 'worldmap.decoration.CRATER',
  DUNE: 'worldmap.decoration.DUNE',
};

function decorationLabel(kind: DecorationKind | null | undefined): string {
  if (!kind) return t('worldmap.decoration.unknown');
  const key = DECORATION_KEYS[kind];
  return key ? t(key) : kind;
}

/**
 * `Colony.barbarianFortArtId` values are "barbarian-fort-N"; the actual art
 * file is "barbarian-map-icon-N.webp" (a separate, smaller asset keyed to the
 * same N). A single source of truth for that filename mapping, used by both
 * the full-map marker (getBarbarianMapIcon) and the popover (renderPopoverContent)
 * so the two can never show different artwork for the same settlement again —
 * they previously each resolved a different file ("barbarian-fort-N.webp" in
 * the popover vs. this one on the map), which was a real visual mismatch, not
 * a deliberate two-resolution pairing.
 */
export function barbarianMapIconSrc(fortArtId: string): string {
  return `/barbarian/${fortArtId.replace('barbarian-fort-', 'barbarian-map-icon-')}.webp`;
}

const cellKey = (x: number, y: number): string => `${x},${y}`;

/** Tap vs. drag disambiguation threshold, in CSS px. */
const DRAG_THRESHOLD = 6;

/** Wheel-to-zoom sensitivity: deltaY * this, exponentiated, gives the zoom multiplier for one tick. */
const WHEEL_ZOOM_SENSITIVITY = 0.001;

/** A marker's half-width/half-height as a fraction of its full grid-cell footprint, leaving a gap between adjacent markers. */
const MARKER_SIZE_RATIO = 0.4;

/** Vertical gap (canvas px) between a marker/icon's bottom edge and its colony-name label. */
const COLONY_LABEL_GAP_PX = 2;

/** A barbarian fort icon's on-screen width as a multiple of its cell's own width — kept well under 1 (unlike MARKER_SIZE_RATIO's plain diamonds, which are smaller still) since bot/barbarian camps routinely sit in adjacent cells, and two neighbors at a ratio approaching 1 would touch or overlap (the closest two plots ever get is exactly one tile width apart). */
const BARBARIAN_ICON_CELL_RATIO = 0.55;

/**
 * CLAUDE.md's mobile-first minimum tap target. Under the iso projection
 * every cell is the SAME on-screen width (see isoTileSizePx) — unlike the
 * old trapezoid's row-dependent taper, there's no "narrowest row" worst case
 * to reason about, just one tile size at a given zoom. resize() recomputes
 * the actual zoom ceiling from it, on any viewport.
 */
export const MIN_TAP_TARGET_PX = 44;

function distanceBetween(a: { x: number; y: number }, b: { x: number; y: number }): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function midpoint(a: { x: number; y: number }, b: { x: number; y: number }): { x: number; y: number } {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

/**
 * Clicking the (flat) minimap targets an image pixel; centering it in the
 * iso main view means converting that pixel to a grid position (the same
 * linear band mapping gridPositionToImagePixel uses, inverted) and then
 * through isoGridToProjected — solved for pan instead of screen position,
 * same idea isoToScreen uses forward. A standalone, closure-free export
 * (rather than inline in jumpToMinimapPoint) so it can be unit-tested
 * directly, including from worldmap-screen.test.ts to derive expected click
 * coordinates instead of hand-computing them.
 */
export function computeMinimapJumpPan(
  minimapX: number,
  minimapY: number,
  minimapWidth: number,
  minimapHeight: number,
  displayWidth: number,
  displayHeight: number,
  viewportWidth: number,
  viewportHeight: number,
): PanOffset {
  const u = Math.min(MARS_IMAGE_WIDTH, Math.max(0, minimapWidth > 0 ? (minimapX / minimapWidth) * MARS_IMAGE_WIDTH : 0));
  const v = Math.min(MARS_IMAGE_HEIGHT, Math.max(0, minimapHeight > 0 ? (minimapY / minimapHeight) * MARS_IMAGE_HEIGHT : 0));
  const t = Math.min(1, Math.max(0, (v - MARS_BAND_TOP) / (MARS_BAND_BOTTOM - MARS_BAND_TOP)));
  const gx = (u / MARS_IMAGE_WIDTH) * GRID_WIDTH;
  const gy = t * GRID_HEIGHT;
  const p = isoGridToProjected(gx, gy);
  const rawPan: PanOffset = {
    x: viewportWidth / 2 - p.x * displayWidth - displayWidth / 2,
    y: viewportHeight / 2 - p.y * displayHeight,
  };
  return clampPanOffset(rawPan, viewportWidth, viewportHeight, displayWidth, displayHeight);
}

export function renderWorldMapScreen(root: HTMLElement): () => void {
  // Full-bleed: fills the shell's entire content area edge-to-edge (no
  // max-width/mx-auto/side padding like other screens) — the map itself is
  // the primary surface, not content floating in a padded card.
  root.innerHTML = `
    <div class="w-full h-full flex flex-col">
      <div class="flex items-center justify-between gap-2 px-3 py-2 border-b border-border shrink-0">
        <h1 class="font-heading text-gold text-base">${t('worldmap.title')}</h1>
        <div id="worldmap-status" class="text-text-dim text-xs" role="status">${t('common.loading')}</div>
      </div>

      <div class="flex flex-wrap gap-x-3 gap-y-1 px-3 py-1.5 text-[10px] text-text-dim border-b border-border shrink-0">
        <span class="flex items-center gap-1"><span class="font-heading text-success">Aa</span>${t('worldmap.legend.yourColony')}</span>
        <span class="flex items-center gap-1"><span class="font-heading text-text">Aa</span>${t('worldmap.legend.otherColony')}</span>
        <span class="flex items-center gap-1"><span class="inline-block w-2.5 h-2.5 rounded-sm bg-purple border border-purple-light"></span>${t('worldmap.legend.resourceNode')}</span>
        <span class="flex items-center gap-1"><span class="inline-block w-2.5 h-2.5 rounded-sm" style="background:#6b6259"></span>${t('worldmap.legend.terrain')}</span>
      </div>

      <div id="worldmap-wrap" class="relative flex-1 w-full min-h-0 overflow-hidden bg-surface-2">
        <canvas id="worldmap-canvas" class="w-full h-full block touch-none select-none"></canvas>
        <canvas id="worldmap-clouds" aria-hidden="true" class="pointer-events-none absolute inset-0 w-full h-full"></canvas>
        <button type="button" id="worldmap-focus-home" aria-label="${t('worldmap.focusHome.aria')}"
          class="absolute bottom-2 left-2 z-10 min-w-11 min-h-11 px-3 flex items-center gap-1.5 bg-surface-2/90 border border-border rounded shadow-lift text-text text-xs touch-manipulation hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-surface-2/90">
          ${t('worldmap.focusHome')}
        </button>
        <span id="worldmap-home-marker" hidden aria-hidden="true"
          class="pointer-events-none absolute z-10 w-10 h-10 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-gold-bright shadow-glow animate-pulse motion-reduce:animate-none"></span>
        <aside id="worldmap-home-notice" aria-live="polite" aria-labelledby="worldmap-home-notice-title" hidden
          class="absolute z-20 w-64 max-w-[calc(100%-1rem)] p-3 bg-surface/95 border border-gold rounded-lg shadow-lift text-left">
          <div id="worldmap-home-notice-title" class="font-heading text-gold text-sm mb-1">${t('worldmap.homeNotice.title')}</div>
          <p class="text-xs text-text mb-2">${t('worldmap.homeNotice.body')}</p>
          <button type="button" id="worldmap-home-notice-dismiss"
            class="min-h-11 min-w-11 px-3 bg-gold text-bg text-xs font-bold rounded touch-manipulation hover:bg-gold-bright">${t('worldmap.homeNotice.dismiss')}</button>
        </aside>
        <section id="worldmap-boost-hud" data-boost-hud hidden class="absolute left-2 z-10 flex flex-col items-start gap-1.5"></section>
        <canvas id="worldmap-minimap-canvas" class="absolute bottom-2 right-2 w-32 h-16 sm:w-40 sm:h-20 rounded border border-border/80 bg-surface-2 shadow-lift touch-none select-none z-10" aria-label="${t('worldmap.minimap.aria')}"></canvas>
      </div>
    </div>
  `;

  const statusEl = root.querySelector('#worldmap-status') as HTMLElement;
  const wrap = root.querySelector('#worldmap-wrap') as HTMLDivElement;
  const canvas = root.querySelector('#worldmap-canvas') as HTMLCanvasElement;
  const cloudCanvas = root.querySelector('#worldmap-clouds') as HTMLCanvasElement;
  const focusHomeBtn = root.querySelector('#worldmap-focus-home') as HTMLButtonElement;
  const minimapCanvas = root.querySelector('#worldmap-minimap-canvas') as HTMLCanvasElement;
  const homeNoticeEl = root.querySelector('#worldmap-home-notice') as HTMLElement;
  const homeMarkerEl = root.querySelector('#worldmap-home-marker') as HTMLSpanElement;
  const homeNoticeDismissBtn = root.querySelector('#worldmap-home-notice-dismiss') as HTMLButtonElement;
  const ctx = canvas.getContext('2d');
  const minimapCtx = minimapCanvas.getContext('2d');
  const cloudCtx = cloudCanvas.getContext('2d');
  const stopBoostHud = mountBoostHud(root.querySelector('#worldmap-boost-hud') as HTMLElement);

  if (!ctx) {
    statusEl.textContent = t('worldmap.canvasUnsupported');
    return () => {};
  }

  let disposed = false;
  let pois: WorldMapPoi[] = [];
  let poiByCell = new Map<string, WorldMapPoi>();
  let myColonyId: string | null = null;
  let zoom = DEFAULT_ZOOM;
  let panOffset: PanOffset = { x: 0, y: 0 };
  /** Centers the initial view on the grid (see resize()) — false again clamps as usual, never re-centering a pan the player already made. */
  let panInitialized = false;
  /**
   * Screen px per source-image px on the X axis at zoom = 1 — "fit the
   * near/widest edge to the viewport's width". Recomputed on every resize.
   */
  let baseScale = 1;
  /** Per-viewport zoom ceiling — see MIN_TAP_TARGET_PX. Recomputed in resize(). */
  let effectiveMaxZoom = MAX_ZOOM;
  let marsImage: HTMLImageElement | null = null;
  let marsImageReady = false;
  /** The one dune sprite, drawn upright across each DUNE_LOCATIONS anchor's 2x3 footprint at draw time (see drawIsoMultiCellSpriteUpright) — unlike barbarianMapIcons there's only one variant, so a single image/flag pair is enough. */
  let duneImage: HTMLImageElement | null = null;
  let duneImageReady = false;
  /** The 2 crater sprite variants, picked per-location by index parity (see CRATER_LOCATIONS) — deterministic, not random, since placement itself is fixed/hand-authored. */
  const craterImages: (HTMLImageElement | null)[] = [null, null];
  const craterImagesReady: boolean[] = [false, false];
  /** The one Crimson Dust cave sprite, drawn at a 1x1 footprint on every RESOURCE_NODE cell — only one variant, same single image/flag pair as duneImage. */
  let crimsonDustImage: HTMLImageElement | null = null;
  let crimsonDustImageReady = false;
  /** The 4 mountain sprite variants, picked per-location via mountainArtVariantFor (see MOUNTAIN_LOCATIONS) — deterministic, same rationale as crater. */
  const mountainImages: (HTMLImageElement | null)[] = [null, null, null, null];
  const mountainImagesReady: boolean[] = [false, false, false, false];
  /** Full-map-only marker art — barbarian/abandoned fort renders AND player-colony settlement renders — keyed by its own URL. The minimap stays plain dots for every marker kind. Loaded once; a miss (not yet loaded, or a POI with no art at all) just falls back to the plain diamond marker. */
  const markerArt = new Map<string, HTMLImageElement>();
  /** URLs whose load has already been kicked off, so getMarkerArt never double-requests the same file. */
  const markerArtRequested = new Set<string>();
  /** The POI cell currently under the pointer (hover/non-drag move only — see onCanvasPointerMove) — drives the highlight outline drawn in draw(). Null when the pointer isn't over any content-bearing cell, or while dragging/pinching (hover tracking is pointless mid-gesture and would just fight the drag). */
  let hoveredCell: { x: number; y: number } | null = null;
  let popoverEl: HTMLDivElement | null = null;
  let redrawScheduled = false;
  /**
   * Reused across every draw() call (not allocated per frame — see CLAUDE.md's
   * "no per-frame allocations" rule) for the fog-of-war's gradient-edged
   * reveal holes: canvas 2D has no direct "radial-gradient cutout" primitive,
   * so punching soft-edged holes means compositing on an offscreen layer
   * (destination-out) and drawing the whole result in one drawImage, rather
   * than ragged alpha artifacts from compositing holes directly onto the main
   * canvas on top of already-drawn terrain/markers.
   */
  const fogCanvas = document.createElement('canvas');
  const fogCtx = fogCanvas.getContext('2d');
  /** The revealed area drawn as ONE opaque shape, blurred into the fog so its outer corners round off and its edge fades. */
  const fogMaskCanvas = document.createElement('canvas');
  const fogMaskCtx = fogMaskCanvas.getContext('2d');
  const sectors = sectorsFor(GRID_WIDTH, GRID_HEIGHT, SECTOR_COLS, SECTOR_ROWS);
  let cachedRevealed: Set<number> = new Set();

  // ---- Cloud overlay -------------------------------------------------
  // A separate pointer-transparent canvas above the map, with its own rAF loop,
  // so drifting clouds never force the (on-demand, much heavier) draw() to run
  // every frame. Clouds are anchored to the map (pan/zoom carry them) with a slight
  // parallax; positions are a pure function of time + camera — see cloud-layer.ts.
  const cloudSpecs = buildClouds();
  const cloudPlacements = cloudSpecs.map(() => emptyPlacement());
  /** Reused every frame — the camera the cloud model reads (map rect = pan offset + display size). */
  const cloudView = emptyView();
  const cloudImages: (HTMLImageElement | null)[] = new Array(CLOUD_SPRITE_COUNT).fill(null);
  /** requestAnimationFrame handle while the loop runs, 0 when stopped (hidden tab, reduced motion, disposed). */
  let cloudFrameId = 0;
  let cloudLastDrawMs = 0;
  /** ~30 fps is plenty for slow haze and halves the battery cost on phones. */
  const CLOUD_FRAME_INTERVAL_MS = 33;
  /** Frozen time for the reduced-motion still frame — mid-range so no cloud sits on the invisible wrap seam. */
  const CLOUD_STILL_TIME_MS = 45_000;

  function cloudMotionAllowed(): boolean {
    if (isReduceMotionEnabled()) return false;
    return !(typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function loadCloudImage(index: number): void {
    const img = new Image();
    img.onload = () => {
      if (disposed) return;
      cloudImages[index] = img;
      if (!cloudFrameId) drawClouds(CLOUD_STILL_TIME_MS);
    };
    img.src = `/clouds/cloud-${index + 1}.webp`;
  }

  /** Sized in CSS px at DPR 1 — the haze is soft, so full-DPR backing stores would only cost fill-rate. */
  function resizeClouds(): void {
    if (!cloudCtx) return;
    cloudCanvas.width = Math.max(1, wrap.clientWidth);
    cloudCanvas.height = Math.max(1, wrap.clientHeight);
  }

  function drawClouds(nowMs: number): void {
    if (!cloudCtx) return;
    const width = cloudCanvas.width;
    const height = cloudCanvas.height;
    cloudCtx.clearRect(0, 0, width, height);
    const { displayWidth, displayHeight } = currentDisplaySize();
    cloudView.zoom = zoom;
    cloudView.maxZoom = effectiveMaxZoom;
    cloudView.viewportWidth = width;
    cloudView.viewportHeight = height;
    cloudView.mapX = panOffset.x;
    cloudView.mapY = panOffset.y;
    cloudView.mapWidth = displayWidth;
    cloudView.mapHeight = displayHeight;
    for (let i = 0; i < cloudSpecs.length; i++) {
      const img = cloudImages[cloudSpecs[i].sprite];
      if (!img) continue;
      const c = placeCloud(cloudSpecs[i], nowMs, cloudView, cloudPlacements[i]);
      if (c.alpha < 0.01) continue;
      const drawHeight = (c.width * img.naturalHeight) / img.naturalWidth;
      const left = c.x - c.width / 2;
      const top = c.y - drawHeight / 2;
      if (left > width || left + c.width < 0 || top > height || top + drawHeight < 0) continue;
      cloudCtx.globalAlpha = c.alpha;
      cloudCtx.drawImage(img, left, top, c.width, drawHeight);
    }
    cloudCtx.globalAlpha = 1;
  }

  function cloudTick(nowMs: number): void {
    cloudFrameId = 0;
    if (disposed || document.hidden) return;
    if (!cloudMotionAllowed()) {
      drawClouds(CLOUD_STILL_TIME_MS);
      return;
    }
    if (nowMs - cloudLastDrawMs >= CLOUD_FRAME_INTERVAL_MS) {
      cloudLastDrawMs = nowMs;
      drawClouds(nowMs);
    }
    cloudFrameId = requestAnimationFrame(cloudTick);
  }

  function startCloudLoop(): void {
    if (cloudFrameId || disposed || !cloudCtx || document.hidden) return;
    cloudFrameId = requestAnimationFrame(cloudTick);
  }

  function stopCloudLoop(): void {
    if (cloudFrameId) cancelAnimationFrame(cloudFrameId);
    cloudFrameId = 0;
  }

  function onCloudVisibilityChange(): void {
    if (document.hidden) stopCloudLoop();
    else startCloudLoop();
  }
  /** The iso diamond's overall bounding width/height, in screen px, at the current zoom — height is ALWAYS exactly ISO_HEIGHT_TO_WIDTH_RATIO times width, the fixed 2:1 tile ratio applied to the whole grid (see isoGridToProjected's doc comment). */
  function currentDisplaySize(): { displayWidth: number; displayHeight: number } {
    const displayWidth = MARS_IMAGE_WIDTH * baseScale * zoom;
    return { displayWidth, displayHeight: displayWidth * ISO_HEIGHT_TO_WIDTH_RATIO };
  }

  /** Grid cell -> screen pixel, applying the current pan/zoom on top of the iso unit-space projection. */
  function toScreen(gx: number, gy: number, displayWidth: number, displayHeight: number): { x: number; y: number } {
    return isoToScreen(gx, gy, displayWidth, displayHeight, panOffset);
  }

  function rebuildIndex(): void {
    poiByCell = new Map(pois.map((p) => [cellKey(p.x, p.y), p]));
  }

  /**
   * Loaded once on mount. The layout (baseScale, pan clamping) is already
   * sized against the constant MARS_IMAGE_WIDTH/HEIGHT before this resolves
   * — see resize() — so nothing needs to be re-derived on load, only redrawn
   * with the real image instead of the procedural fallback.
   */
  function loadMarsImage(): void {
    const img = new Image();
    img.onload = () => {
      if (disposed) return;
      marsImage = img;
      marsImageReady = true;
      scheduleRedraw();
      loadHiResMarsImage();
    };
    img.onerror = () => {
      // Procedural terrain stays as the permanent fallback — no retry, this
      // isn't a value worth failing the whole screen over.
      marsImageReady = false;
    };
    img.src = MARS_IMAGE_URL;
  }

  /**
   * Second stage: once the 2K map is showing, fetch the detailed 4K one and swap it in. It is the same art,
   * so the swap is a redraw, not a visible change of picture. The 2K reference is dropped at that moment so
   * its bitmap can be freed — the map never holds both for longer than it takes to swap. Skipped on
   * low-memory / data-saver / very slow connections, and if it fails the 2K simply stays.
   */
  function loadHiResMarsImage(): void {
    const nav = navigator as Navigator & { deviceMemory?: number; connection?: { saveData?: boolean; effectiveType?: string } };
    if (!shouldLoadHiResMap(nav.deviceMemory, nav.connection)) return;
    const hi = new Image();
    hi.decoding = 'async';
    hi.onload = () => {
      if (disposed) return;
      marsImage = hi; // the 2K is now unreferenced
      scheduleRedraw();
    };
    hi.src = MARS_IMAGE_URL_HIRES;
  }

  /** Loaded once on mount, same as loadMarsImage — falls back to the flat DUNE diamond (resolveCellVisual's DECORATION_COLORS.DUNE) until it resolves or if it ever fails. */
  function loadDuneImage(): void {
    const img = new Image();
    img.onload = () => {
      if (disposed) return;
      duneImage = img;
      duneImageReady = true;
      scheduleRedraw();
    };
    img.onerror = () => {
      duneImageReady = false;
    };
    img.src = '/decorations/dune.webp';
  }

  /** Loaded once on mount, same as loadDuneImage — falls back to resolveCellVisual's plain purple RESOURCE_NODE marker until it resolves or if it ever fails. */
  function loadCrimsonDustImage(): void {
    const img = new Image();
    img.onload = () => {
      if (disposed) return;
      crimsonDustImage = img;
      crimsonDustImageReady = true;
      scheduleRedraw();
    };
    img.onerror = () => {
      crimsonDustImageReady = false;
    };
    img.src = '/decorations/crimson-dust.webp';
  }

  /** Loaded once on mount, same as loadDuneImage — falls back to the flat CRATER diamond until it resolves or if it ever fails. */
  function loadCraterImage(index: number, src: string): void {
    const img = new Image();
    img.onload = () => {
      if (disposed) return;
      craterImages[index] = img;
      craterImagesReady[index] = true;
      scheduleRedraw();
    };
    img.onerror = () => {
      craterImagesReady[index] = false;
    };
    img.src = src;
  }

  /** Loaded once on mount, same as loadCraterImage — falls back to the flat MOUNTAIN diamond until it resolves or if it ever fails. */
  function loadMountainImage(index: number, src: string): void {
    const img = new Image();
    img.onload = () => {
      if (disposed) return;
      mountainImages[index] = img;
      mountainImagesReady[index] = true;
      scheduleRedraw();
    };
    img.onerror = () => {
      mountainImagesReady[index] = false;
    };
    img.src = src;
  }

  /**
   * Lazily loads (once per distinct URL actually seen on the map — never a
   * hardcoded file list, so new fort variants and new colony HQ tiers just
   * work) and returns a settlement marker image, or null while it's still
   * loading/hasn't been requested yet (draw() falls back to the plain
   * diamond marker in that case). The popover (`renderPopoverContent` below)
   * resolves the SAME src for the SAME POI, so the two can never show
   * different artwork for one settlement.
   */
  function getMarkerArt(src: string): HTMLImageElement | null {
    const cached = markerArt.get(src);
    if (cached) return cached;
    if (markerArtRequested.has(src)) return null; // load in flight
    markerArtRequested.add(src);
    const img = new Image();
    img.onload = () => {
      if (disposed) return;
      markerArt.set(src, img);
      scheduleRedraw();
    };
    img.src = src;
    return null;
  }

  function scheduleRedraw(): void {
    if (redrawScheduled) return;
    redrawScheduled = true;
    requestAnimationFrame(() => {
      redrawScheduled = false;
      draw();
    });
  }

  /**
   * Background as ONE `drawImage` call covering the iso diamond's bounding
   * rectangle — unlike the old trapezoid projection, the iso transform
   * doesn't taper by row, so there's no per-row slicing to do any more: the
   * orange equatorial band (the only part of the source image that maps to
   * playable grid rows) just gets stretched to fill
   * [panOffset.x, panOffset.x+displayWidth] x [panOffset.y, panOffset.y+displayHeight],
   * exactly the rectangle isoToScreen's own [-0.5,0.5]x[0,1] unit-space
   * convention produces for the grid's 4 corners.
   */
  function drawBackground(displayWidth: number, displayHeight: number): void {
    if (!ctx || !marsImageReady || !marsImage) return;
    // The source rectangle is in 2K "logical" pixels; the 4K image has twice as many real ones per logical one.
    const k = marsScale(marsImage.naturalWidth);
    ctx.drawImage(
      marsImage,
      0,
      MARS_BAND_TOP * k,
      MARS_IMAGE_WIDTH * k,
      (MARS_BAND_BOTTOM - MARS_BAND_TOP) * k,
      panOffset.x,
      panOffset.y,
      displayWidth,
      displayHeight,
    );
  }

  function draw(): void {
    if (disposed || !ctx) return;
    const width = wrap.clientWidth;
    const height = wrap.clientHeight;
    const colors = readThemeColors();
    const { displayWidth, displayHeight } = currentDisplaySize();

    ctx.save();
    ctx.fillStyle = colors.surface2;
    ctx.fillRect(0, 0, width, height);

    if (marsImageReady && marsImage) {
      drawBackground(displayWidth, displayHeight);
    } else {
      // Loading/error fallback — a single rect spanning the iso diamond's
      // overall bounding box is enough; it's temporary.
      ctx.save();
      ctx.translate(panOffset.x, panOffset.y);
      drawTerrainBackground(ctx, displayWidth, displayHeight);
      ctx.restore();
    }

    // Sector overlay removed — the red border lines competed with terrain
    // art and made the map feel like a board game rather than a landscape.

    // Dune features are drawn ONCE each, upright (not warped to the grid's
    // shear — see drawIsoMultiCellSpriteUpright) across their whole 2x3
    // footprint — not per individual POI cell, which is what produced 3 (now
    // 6) disjointed, misaligned boxes before. Drawn before the POI loop so it
    // sits at ground level, same as any other decoration.
    if (duneImageReady && duneImage) {
      for (const anchor of DUNE_LOCATIONS) {
        drawIsoMultiCellSpriteUpright(
          ctx,
          duneImage,
          { minX: anchor.x, minY: anchor.y, width: DUNE_WIDTH_CELLS, height: DUNE_HEIGHT_CELLS },
          displayWidth,
          displayHeight,
          panOffset,
        );
      }
    }
    // Same one-pass-per-feature approach as dune, for the 6 hand-placed
    // crater locations — these fill their own whole footprint (unlike the
    // scattered single-cell pass below, which scales down and jitters).
    for (let i = 0; i < CRATER_LOCATIONS.length; i++) {
      const img = craterImages[i % 2];
      if (!craterImagesReady[i % 2] || !img) continue;
      const loc = CRATER_LOCATIONS[i];
      drawIsoMultiCellSpriteUpright(ctx, img, { minX: loc.x, minY: loc.y, width: loc.width, height: loc.height }, displayWidth, displayHeight, panOffset);
    }
    // Every SCATTERED (not hand-placed) 1x1 CRATER cell also gets real art
    // now — replaces the old flat "black diamond" generic-impassable-terrain
    // render. Scaled down and jittered within its own cell (drawIsoCellSprite)
    // rather than filling the cell edge-to-edge, so the grid doesn't read as
    // a visible lattice of identically-sized, identically-positioned sprites.
    // The per-POI loop below skips re-drawing these once loaded (same
    // craterImagesReady gate as the hand-placed pass above).
    for (const poi of pois) {
      if (poi.decorationKind !== 'CRATER' || craterLocationIndexFor(poi.x, poi.y) !== null) continue;
      const variant = scatteredCraterArtVariantFor(poi.x, poi.y);
      const img = craterImages[variant];
      if (!craterImagesReady[variant] || !img) continue;
      drawIsoCellSprite(ctx, img, poi.x, poi.y, 1, displayWidth, displayHeight, panOffset);
    }
    // Every RESOURCE_NODE (Crimson Dust) cell gets the cave sprite, same
    // scaled-down-and-jittered single-cell approach as scattered craters
    // (a distinct jitter salt so the two kinds' offsets never coincide).
    for (const poi of pois) {
      if (poi.kind !== 'RESOURCE_NODE' || !crimsonDustImageReady || !crimsonDustImage) continue;
      drawIsoCellSprite(ctx, crimsonDustImage, poi.x, poi.y, 2, displayWidth, displayHeight, panOffset);
    }
    // Same one-pass-per-feature approach, for the 12 hand-placed mountain
    // locations (3 instances each of 4 art variants) — the scattered,
    // per-cell-formula 1x1 mountains elsewhere on the map are NOT drawn here
    // (no entry in MOUNTAIN_LOCATIONS for them) and keep rendering as the
    // plain flat diamond, unchanged.
    for (let i = 0; i < MOUNTAIN_LOCATIONS.length; i++) {
      const variant = mountainArtVariantFor(i);
      const img = mountainImages[variant];
      if (!mountainImagesReady[variant] || !img) continue;
      const loc = MOUNTAIN_LOCATIONS[i];
      drawIsoMultiCellSpriteUpright(ctx, img, { minX: loc.x, minY: loc.y, width: loc.width, height: loc.height }, displayWidth, displayHeight, panOffset);
    }

    // Every cell is the same size under iso (see isoTileSizePx), so this is
    // computed once per draw() call rather than per POI, unlike the old
    // trapezoid's row-dependent rowWidthRatio(t).
    const { width: cellWidthPx, height: rowHeightPx } = isoTileSizePx(displayWidth, displayHeight);
    for (const poi of pois) {
      // Already drawn in the dedicated pass above — skip the per-cell
      // diamond entirely once the sprite is loaded. Before it loads, fall
      // through to the normal flat diamond (DECORATION_COLORS.DUNE) so the
      // feature still shows something instead of empty ground.
      if (poi.decorationKind === 'DUNE' && duneImageReady && duneImage) continue;
      if (poi.decorationKind === 'CRATER') {
        const locIndex = craterLocationIndexFor(poi.x, poi.y);
        const variant = locIndex !== null ? locIndex % 2 : scatteredCraterArtVariantFor(poi.x, poi.y);
        if (craterImagesReady[variant]) continue;
      }
      if (poi.decorationKind === 'MOUNTAIN') {
        const locIndex = mountainLocationIndexFor(poi.x, poi.y);
        if (locIndex !== null && mountainImagesReady[mountainArtVariantFor(locIndex)]) continue;
      }
      if (poi.kind === 'RESOURCE_NODE' && crimsonDustImageReady) continue;
      const visual = resolveCellVisual(poi, myColonyId, colors);
      if (!visual) continue;
      const { x, y } = toScreen(poi.x + 0.5, poi.y + 0.5, displayWidth, displayHeight);
      // Barbarian/bot-camp settlements get their assigned fort art instead
      // of the plain ownership diamond — a raid destination worth spotting
      // at a glance on the full map (the minimap stays plain dots; it's too
      // small for a photographic icon to read at all). barbarianFortArtId is
      // only ever set for a true barbarian or a bot camp (see
      // ColonyService.createColonyForUser / abandonColony / BarbarianService),
      // never a real player colony, so truthiness alone is the right gate —
      // this is what makes a bot camp read identically to a true barbarian.
      //
      // A REAL player colony takes the other branch: the two are mutually
      // exclusive by construction (a colony has fort art or an owner, never
      // both), so the barbarian check staying first is what makes that
      // precedence explicit here rather than only in a comment. Its art is
      // picked by the occupier's Command Center level, so a settlement
      // visibly grows as its HQ does.
      const fortArt = poi.barbarianFortArtId ? getMarkerArt(barbarianMapIconSrc(poi.barbarianFortArtId)) : null;
      const colonyArt = !poi.barbarianFortArtId && poi.colonyId ? colonyMapArtFor(poi.hqLevel) : null;
      const colonyIcon = colonyArt ? getMarkerArt(colonyArt.src) : null;
      const icon = fortArt ?? colonyIcon;
      let markerHalfWidthPx: number;
      let markerBottomY: number;
      if (icon) {
        const iconWidth = cellWidthPx * (colonyArt && !fortArt ? colonyArt.cellRatio : BARBARIAN_ICON_CELL_RATIO);
        const iconHeight = iconWidth * (icon.height / icon.width);
        // No ground pad: settlements stand directly on the terrain. Ownership
        // moved to the name's colour (below), which is why removing the pad
        // does not cost the map its mine-vs-theirs signal.
        ctx.drawImage(icon, x - iconWidth / 2, y - iconHeight / 2, iconWidth, iconHeight);
        markerHalfWidthPx = iconWidth / 2;
        markerBottomY = y + iconHeight / 2;
      } else {
        const halfWidth = cellWidthPx * MARKER_SIZE_RATIO;
        const halfHeight = rowHeightPx * MARKER_SIZE_RATIO;
        drawCell(ctx, visual, x, y, halfWidth, halfHeight);
        markerHalfWidthPx = halfWidth;
        markerBottomY = y + halfHeight;
      }
      // Only colonized COLONY_PLOT POIs ever carry a colonyName (resource
      // nodes/decorations never do) — covers both active and barbarian/
      // abandoned colonies, which keep their original name.
      //
      // The player's own colony is named in green; everyone else's in the
      // ordinary text colour. This IS the ownership signal now that the
      // gold/crimson ground pad is gone — so it is read from the same
      // `myColonyId` the popover's own `isMine` test uses, not re-derived.
      if (poi.colonyName) {
        const isMine = !!poi.colonyId && poi.colonyId === myColonyId;
        drawColonyLabel(
          ctx,
          poi.colonyName,
          x,
          markerBottomY + COLONY_LABEL_GAP_PX,
          markerHalfWidthPx,
          isMine ? colors.success : colors.text,
        );
      }
    }
    ctx.restore();

    // Fog of war — sector-based. Sectors open in adjacency order from the
    // top-left (see revealedSectors); everything not yet open is fogged.
    // cachedRevealed is recomputed in load() when POI data arrives.
    const revealed = cachedRevealed;
    if (fogCtx) {
      if (fogCanvas.width !== canvas.width || fogCanvas.height !== canvas.height) {
        fogCanvas.width = canvas.width;
        fogCanvas.height = canvas.height;
      }
      const dpr = window.devicePixelRatio || 1;
      fogCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
      fogCtx.globalCompositeOperation = 'source-over';
      fogCtx.clearRect(0, 0, width, height);
      fogCtx.fillStyle = 'rgba(10, 6, 4, 0.55)';
      fogCtx.fillRect(0, 0, width, height);
      fogCtx.globalCompositeOperation = 'destination-out';
      fogCtx.fillStyle = 'rgba(0,0,0,1)';
      // Feather radius follows the sector size on screen, so the rounding reads the same at any zoom.
      const sample = sectors[0];
      const cellW = sample ? toScreen(sample.maxX + 1, 0, displayWidth, displayHeight).x - toScreen(sample.minX, 0, displayWidth, displayHeight).x : 0;
      const featherPx = Math.min(36, Math.max(8, Math.abs(cellW) * 0.1));
      const canFeather =
        !!fogMaskCtx && typeof fogCtx.filter === 'string' && revealed.size > 0;
      if (canFeather && fogMaskCtx) {
        if (fogMaskCanvas.width !== canvas.width || fogMaskCanvas.height !== canvas.height) {
          fogMaskCanvas.width = canvas.width;
          fogMaskCanvas.height = canvas.height;
        }
        fogMaskCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
        fogMaskCtx.clearRect(0, 0, width, height);
        fogMaskCtx.fillStyle = '#000';
        // Sectors on the map's own border grow outward past it, so the blur fades the
        // INNER boundary only and never leaves a fog line along the map edge.
        const pad = featherPx * 3;
        for (const si of revealed) {
          const sec = sectors[si];
          const tl = toScreen(sec.minX, sec.minY, displayWidth, displayHeight);
          const br = toScreen(sec.maxX + 1, sec.maxY + 1, displayWidth, displayHeight);
          const x0 = tl.x - (sec.minX === 0 ? pad : 0);
          const y0 = tl.y - (sec.minY === 0 ? pad : 0);
          const x1 = br.x + (sec.maxX + 1 >= GRID_WIDTH ? pad : 0);
          const y1 = br.y + (sec.maxY + 1 >= GRID_HEIGHT ? pad : 0);
          fogMaskCtx.fillRect(x0, y0, x1 - x0, y1 - y0);
        }
        fogCtx.save();
        fogCtx.setTransform(1, 0, 0, 1, 0, 0);
        fogCtx.filter = `blur(${(featherPx * dpr).toFixed(1)}px)`;
        fogCtx.drawImage(fogMaskCanvas, 0, 0);
        fogCtx.restore();
      } else {
        // No canvas filters (older Safari): the previous hard-edged cut-out.
        for (const si of revealed) {
          const s = sectors[si];
          const tl = toScreen(s.minX, s.minY, displayWidth, displayHeight);
          const tr = toScreen(s.maxX + 1, s.minY, displayWidth, displayHeight);
          const br = toScreen(s.maxX + 1, s.maxY + 1, displayWidth, displayHeight);
          const bl = toScreen(s.minX, s.maxY + 1, displayWidth, displayHeight);
          fogCtx.beginPath();
          fogCtx.moveTo(tl.x, tl.y);
          fogCtx.lineTo(tr.x, tr.y);
          fogCtx.lineTo(br.x, br.y);
          fogCtx.lineTo(bl.x, bl.y);
          fogCtx.closePath();
          fogCtx.fill();
        }
      }
      ctx.drawImage(fogCanvas, 0, 0, canvas.width, canvas.height, 0, 0, width, height);
    }

    // No hover outline is drawn. A gold diamond used to be stroked over the
    // hovered cell here, but it read as a selection marker rather than a
    // hover hint and cluttered every POI on the map. `hoveredCell` is still
    // tracked in onCanvasPointerMove purely to drive the pointer cursor,
    // which keeps the clickability affordance without drawing anything.

    // Repaint the clouds in the SAME frame as the map. They are anchored to the
    // ground, so if only their own ~30 fps loop moved them they would visibly lag
    // the terrain during a pan or pinch. (Also covers reduced motion, where no loop runs.)
    drawClouds(cloudMotionAllowed() ? performance.now() : CLOUD_STILL_TIME_MS);

    positionHomeNotice(displayWidth, displayHeight);
    drawMinimap(colors, displayWidth, displayHeight);
  }

  /**
   * Keeps the one-time "first home colony" callout (and its pulsing ring)
   * pinned to the home tile through every pan/zoom redraw. The card sits
   * above the tile, flipping below it near the top edge, and is clamped
   * horizontally so it never leaves the map on a narrow phone.
   */
  function positionHomeNotice(displayWidth: number, displayHeight: number): void {
    if (homeNoticeEl.hidden) return;
    const home = pois.find((p) => p.colonyId === myColonyId);
    if (!home) return;
    const centre = toScreen(home.x + 0.5, home.y + 0.5, displayWidth, displayHeight);
    homeMarkerEl.style.left = `${centre.x}px`;
    homeMarkerEl.style.top = `${centre.y}px`;

    const gap = 28;
    const cardW = homeNoticeEl.offsetWidth || 256;
    const cardH = homeNoticeEl.offsetHeight || 120;
    const left = Math.min(Math.max(8, centre.x - cardW / 2), Math.max(8, wrap.clientWidth - cardW - 8));
    const above = centre.y - gap - cardH;
    const top = above >= 8 ? above : centre.y + gap;
    homeNoticeEl.style.left = `${left}px`;
    homeNoticeEl.style.top = `${top}px`;
  }

  function showHomeNoticeIfPending(): void {
    if (!isHomeNoticePending(myColonyId) || !pois.some((p) => p.colonyId === myColonyId)) return;
    homeNoticeEl.hidden = false;
    homeMarkerEl.hidden = false;
    scheduleRedraw();
  }

  function onDismissHomeNotice(): void {
    dismissHomeNotice();
    homeNoticeEl.hidden = true;
    homeMarkerEl.hidden = true;
  }

  /**
   * The minimap stays a flat top-down overview (drawn at the source image's
   * own native, untapered proportions) — the viewport indicator rectangle is
   * therefore only an approximation of the main view's diamond footprint: it
   * converts the 4 screen corners through the (exact) iso inverse to grid
   * space, then to image-pixel space, and takes their bounding box. That's
   * honest about what it's showing (a rough "you are here", not a precise
   * readout) rather than pretending the rotation doesn't exist.
   */
  function computeMinimapRect(minimapWidth: number, minimapHeight: number, displayWidth: number, displayHeight: number): MinimapRect {
    const corners = [
      { x: 0, y: 0 },
      { x: wrap.clientWidth, y: 0 },
      { x: 0, y: wrap.clientHeight },
      { x: wrap.clientWidth, y: wrap.clientHeight },
    ];
    const imgPts = corners.map(({ x, y }) => {
      const xu = displayWidth > 0 ? (x - panOffset.x - displayWidth / 2) / displayWidth : 0;
      const yu = displayHeight > 0 ? (y - panOffset.y) / displayHeight : 0;
      const grid = isoProjectedToGridContinuous(xu, yu);
      return gridPositionToImagePixel(grid.x, grid.y);
    });
    const mmScaleX = minimapWidth / MARS_IMAGE_WIDTH;
    const mmScaleY = minimapHeight / MARS_IMAGE_HEIGHT;
    const xs = imgPts.map((p) => p.x * mmScaleX);
    const ys = imgPts.map((p) => p.y * mmScaleY);
    const clampX = (v: number) => Math.min(minimapWidth, Math.max(0, v));
    const clampY = (v: number) => Math.min(minimapHeight, Math.max(0, v));
    const x = clampX(Math.min(...xs));
    const y = clampY(Math.min(...ys));
    return { x, y, width: Math.max(0, clampX(Math.max(...xs)) - x), height: Math.max(0, clampY(Math.max(...ys)) - y) };
  }

  function drawMinimap(colors: ReturnType<typeof readThemeColors>, displayWidth: number, displayHeight: number): void {
    if (!minimapCtx) return;
    const width = minimapCanvas.clientWidth;
    const height = minimapCanvas.clientHeight;
    if (width <= 0 || height <= 0) return;

    minimapCtx.save();
    minimapCtx.fillStyle = colors.surface2;
    minimapCtx.fillRect(0, 0, width, height);
    if (marsImageReady && marsImage) {
      // Flat/top-down — the source image is already a flat equirectangular
      // projection, so it drops in with a plain stretched drawImage. Shows
      // the full planet including both poles, unlike the tilted main view.
      minimapCtx.drawImage(marsImage, 0, 0, width, height);
    }

    const mmScaleX = width / MARS_IMAGE_WIDTH;
    const mmScaleY = height / MARS_IMAGE_HEIGHT;
    const MINIMAP_MARKER_RADIUS = 2;
    // Deliberately plain dots for every marker kind here, barbarian
    // settlements included — the minimap is too small for a photographic
    // icon to read as anything but noise; that distinction lives on the
    // full map instead (see draw()'s BARBARIAN_ICON_CELL_RATIO branch).
    for (const poi of pois) {
      const visual = resolveCellVisual(poi, myColonyId, colors);
      if (!visual) continue;
      const imgPos = gridPositionToImagePixel(poi.x + 0.5, poi.y + 0.5);
      minimapCtx.fillStyle = visual.fill;
      minimapCtx.beginPath();
      minimapCtx.arc(imgPos.x * mmScaleX, imgPos.y * mmScaleY, MINIMAP_MARKER_RADIUS, 0, Math.PI * 2);
      minimapCtx.fill();
    }

    const rect = computeMinimapRect(width, height, displayWidth, displayHeight);
    if (rect.width > 0 && rect.height > 0) {
      minimapCtx.strokeStyle = colors.goldBright;
      minimapCtx.lineWidth = 1.5;
      minimapCtx.strokeRect(rect.x, rect.y, rect.width, rect.height);
    }
    minimapCtx.restore();
  }

  function resizeMinimap(): void {
    if (!minimapCtx) return;
    const width = minimapCanvas.clientWidth;
    const height = minimapCanvas.clientHeight;
    if (width <= 0 || height <= 0) return;
    const dpr = window.devicePixelRatio || 1;
    minimapCanvas.width = Math.max(1, Math.round(width * dpr));
    minimapCanvas.height = Math.max(1, Math.round(height * dpr));
    minimapCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  function resize(): void {
    if (!ctx) return;
    const width = wrap.clientWidth;
    const height = wrap.clientHeight;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.round(width * dpr));
    canvas.height = Math.max(1, Math.round(height * dpr));
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    // Size so DEFAULT_ZOOM shows DEFAULT_VISIBLE_WIDTH_FRACTION of the map's width.
    baseScale = width > 0 ? width / (MARS_IMAGE_WIDTH * DEFAULT_VISIBLE_WIDTH_FRACTION) : 1;
    // Every cell is the same width under iso (isoTileSizePx), so there's no
    // "narrowest row" worst case any more — just solve tileWidthPx(zoom) ===
    // MIN_TAP_TARGET_PX directly: tileWidthPx = displayWidth/(GRID_WIDTH*(1+shear))
    // = 2*displayWidth/(2*GRID_WIDTH+GRID_HEIGHT) (shear = GRID_HEIGHT/(2*GRID_WIDTH),
    // see isoTileSizePx/shearFraction in worldmap-renderer.ts), and
    // displayWidth = MARS_IMAGE_WIDTH*baseScale*zoom, substituting
    // MARS_IMAGE_WIDTH*baseScale = width/DEFAULT_VISIBLE_WIDTH_FRACTION gives
    // this closed form. Scaled by DEFAULT_VISIBLE_WIDTH_FRACTION to match
    // baseScale above.
    effectiveMaxZoom =
      width > 0
        ? Math.max(
            MAX_ZOOM,
            (MIN_TAP_TARGET_PX * (2 * GRID_WIDTH + GRID_HEIGHT) * DEFAULT_VISIBLE_WIDTH_FRACTION) / (2 * width),
          )
        : MAX_ZOOM;
    zoom = clampZoom(zoom, effectiveMaxZoom);
    const { displayWidth, displayHeight } = currentDisplaySize();
    if (!panInitialized) {
      // DEFAULT_ZOOM now shows only a fraction of the map, so the default
      // {0,0} pan would pin the view to the map's far top-left corner
      // instead of showing its middle — center it on first layout instead.
      panOffset = { x: width / 2 - displayWidth / 2, y: height / 2 - displayHeight / 2 };
      panInitialized = true;
    }
    panOffset = clampPanOffset(panOffset, width, height, displayWidth, displayHeight);
    resizeMinimap();
    resizeClouds();
    scheduleRedraw();
  }

  /** Re-derive the pan offset for a new zoom level, anchored so the point under `anchor` (canvas-space) stays visually still. */
  function applyZoom(newZoomRaw: number, anchor: PanOffset): void {
    const newZoom = clampZoom(newZoomRaw, effectiveMaxZoom);
    if (newZoom === zoom) return;
    const rawPan = computeZoomedPan(zoom, newZoom, panOffset, anchor);
    zoom = newZoom;
    const { displayWidth, displayHeight } = currentDisplaySize();
    panOffset = clampPanOffset(rawPan, wrap.clientWidth, wrap.clientHeight, displayWidth, displayHeight);
    scheduleRedraw();
  }

  // ---- Popover -------------------------------------------------------

  function closePopover(): void {
    popoverEl?.remove();
    popoverEl = null;
  }

  function renderPopoverContent(gx: number, gy: number, poi: WorldMapPoi | undefined): string {
    const header = `
      <div class="flex items-start justify-between gap-2 mb-2">
        <div class="font-heading text-gold text-sm">(${gx}, ${gy})</div>
        <button type="button" data-action="close" aria-label="${t('common.close')}"
          class="min-w-11 min-h-11 -m-2 flex items-center justify-center text-text-dim hover:text-text leading-none text-lg">&times;</button>
      </div>
    `;
    if (!poi) {
      return `${header}<p class="text-text-dim text-xs">${t('worldmap.popover.empty')}</p>`;
    }
    if (poi.kind === 'DECORATION') {
      // Every DECORATION kind except DUNE has no mechanical pathing
      // enforcement anywhere either (march distance is pure Euclidean,
      // terrain-unaware) — "impassable" has always been cosmetic copy, not
      // a real guard. DUNE's copy is the honest version of that: passable,
      // the only real rule is "not a COLONY_PLOT", same as every decoration.
      const terrainLine =
        poi.decorationKind === 'DUNE' ? t('worldmap.popover.passable') : t('worldmap.popover.impassable');
      return `${header}<p class="text-text-dim text-xs mb-1">${decorationLabel(poi.decorationKind)}</p><p class="text-xs">${terrainLine}</p>`;
    }
    if (poi.kind === 'RESOURCE_NODE') {
      // A cave is pillaged, never claimed or held. It carries a colonyId of
      // its own (an ownerless ACTIVE colony — see Colony.isResourceNode),
      // which is what makes it a legal march target at all; the stock is
      // shared, so a second raider finds what the first one left behind.
      const canRaid = !!poi.colonyId && !!myColonyId;
      return `
        ${header}
        <p class="text-text-dim text-xs mb-1">${t('worldmap.popover.resourceNode')}</p>
        <p class="mb-2">${resourceLabel(poi.resourceType)}</p>
        <p class="text-text-dim text-xs ${canRaid ? 'mb-3' : ''}">${t('worldmap.popover.resourceHint')}</p>
        ${canRaid
          ? `<button type="button" data-action="send-army" class="font-button w-full min-h-11 px-3 py-2 bg-crimson text-bg rounded hover:opacity-90 transition-opacity duration-[120ms]">${t('worldmap.popover.sendArmy')}</button>`
          : ''}
      `;
    }
    const isMine = !!poi.colonyId && poi.colonyId === myColonyId;
    // Owner: the name in bold with their equipped title as a badge under it (the same plate as the profile card).
    const ownerText = (value: string): string => value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const ownership = !poi.colonyId
      ? t('worldmap.owner.unclaimed')
      : `<span class="block text-[10px] text-text-dim uppercase tracking-wide">${t('worldmap.popover.owner')}</span>
         <span class="block font-bold text-text" data-popover-owner>${ownerText(isMine ? t('worldmap.legend.yourColony') : targetOwnerName(poi))}</span>
         ${targetHasOwnerTitle(poi) ? `<span class="block mt-1" data-popover-title>${titleBadgeHtml({ label: poi.ownerTitle as string, rarity: poi.ownerTitleRarity }, 'title-badge--popup')}</span>` : ''}`;
    // A valid march target needs both an actual defender colony (colonyId
    // set — an unclaimed plot has nothing to send an army at) and a colony
    // of our own to send it from (colonyless accounts can browse the map,
    // per resolveWorldId's fallback, but have no roster to draw units from).
    const canSendArmy = !!poi.colonyId && !isMine && !!myColonyId;
    // A true barbarian (seeded or AFK-decayed) or a bot camp — both read the
    // same way to a viewer — shows its once-assigned fort art. See the
    // matching comment on the map-icon gate above for why truthiness alone
    // is the right check.
    // A real player colony shows its own settlement render instead, from the
    // SAME tier the canvas marker resolved — one source of truth per POI.
    const settlementArt = poi.barbarianFortArtId
      ? `<img src="${barbarianMapIconSrc(poi.barbarianFortArtId)}" alt="${t('worldmap.popover.barbarianArt')}" class="popover-art w-[72%] mx-auto block rounded mb-2" />`
      : poi.colonyId
        ? `<img src="${colonyMapArtFor(poi.hqLevel).src}" alt="${t('worldmap.popover.colonyArt')}" class="popover-art w-[72%] mx-auto block mb-2" />`
        : '';
    return `
      ${header}
      <p class="text-text-dim text-xs mb-1">${t('worldmap.popover.colonyPlot')}</p>
      ${settlementArt}
      <p class="${canSendArmy ? 'mb-3' : ''}">${ownership}</p>
      ${canSendArmy
        ? `<button type="button" data-action="send-army" class="font-button w-full min-h-11 px-3 py-2 bg-crimson text-bg rounded hover:opacity-90 transition-opacity duration-[120ms]">${t('worldmap.popover.sendArmy')}</button>`
        : ''}
    `;
  }

  function wirePopoverButtons(poi: WorldMapPoi | undefined): void {
    if (!popoverEl) return;
    const closeBtn = popoverEl.querySelector('[data-action="close"]');
    closeBtn?.addEventListener('click', closePopover);
    const sendArmyBtn = popoverEl.querySelector('[data-action="send-army"]') as HTMLButtonElement | null;
    if (sendArmyBtn && poi) {
      sendArmyBtn.addEventListener('click', () => {
        closePopover();
        openSendArmyModal(root, poi, () => {
          // Nothing to refresh on the map itself — sending an army doesn't
          // change territory/POI ownership (that only happens on
          // resolution, surfaced later on the Battle tab's history list).
          // The new march does appear in the HUD beside the boosts, though.
          window.dispatchEvent(new Event('marches:refresh'));
        });
      });
    }
  }

  /**
   * The map's own HUD, as boxes in the map's coordinates: the header and title-strip patches, the bottom menu's
   * buttons, the minimap, Focus home, the chat pill and the boost chips. A popover may sit on none of them.
   * Hidden elements have no box and drop out.
   */
  function hudBoxes(wrapRect: DOMRect): Box[] {
    const selectors = [
      '#worldmap-minimap-canvas',
      '#worldmap-focus-home',
      '#chat-widget-pill',
      '#worldmap-boost-hud',
      'header.holo-bar > *',
      '#shell-tabbar .shell-tab-btn',
      '#shell-content > div > .border-b.border-border.shrink-0 > *',
    ];
    const boxes: Box[] = [];
    for (const el of document.querySelectorAll(selectors.join(','))) {
      const r = el.getBoundingClientRect();
      if (r.width <= 0 || r.height <= 0) continue;
      boxes.push({ left: r.left - wrapRect.left, top: r.top - wrapRect.top, right: r.right - wrapRect.left, bottom: r.bottom - wrapRect.top });
    }
    return boxes;
  }

  function placePopoverEl(el: HTMLElement, localX: number, localY: number): void {
    const wrapRect = wrap.getBoundingClientRect();
    // Layout size, not getBoundingClientRect(): the card's entrance animation scales it, which would under-measure it.
    const pos = placePopover({
      anchor: { x: localX, y: localY },
      size: { width: el.offsetWidth, height: el.offsetHeight },
      bounds: { width: wrapRect.width, height: wrapRect.height },
      blocked: hudBoxes(wrapRect),
    });
    el.style.left = `${pos.left}px`;
    el.style.top = `${pos.top}px`;
  }

  function openPopover(gx: number, gy: number, clientX: number, clientY: number): void {
    // Goes through the shared SFX pool rather than a local Audio element, so
    // this click follows the settings slider and the mute toggle like every
    // other one. It used to hold its own element at a hardcoded 0.3 volume,
    // which ignored both. playClick() swallows autoplay rejections and a
    // `play()` that returns undefined (jsdom), so it cannot block the popover.
    playClick();
    closePopover();
    const poi = poiByCell.get(cellKey(gx, gy));
    const wrapRect = wrap.getBoundingClientRect();
    const localX = clientX - wrapRect.left;
    const localY = clientY - wrapRect.top;

    const el = document.createElement('div');
    el.className = 'absolute z-10 w-[min(260px,calc(100%-16px))] bg-surface border border-border rounded-lg shadow-lift p-3 text-sm text-text popup-enter';
    el.innerHTML = renderPopoverContent(gx, gy, poi);
    wrap.appendChild(el);
    popoverEl = el;

    placePopoverEl(el, localX, localY);
    // The art has no intrinsic size until it loads, so the card grows then: place it again with its real height.
    el.querySelectorAll('img').forEach((img) => img.addEventListener('load', () => {
      if (popoverEl === el) placePopoverEl(el, localX, localY);
    }));

    wirePopoverButtons(poi);
  }

  // ---- Pointer / pan / tap / pinch --------------------------------------

  /** Client-space (page) positions of every pointer currently down on the main canvas, keyed by pointerId. */
  const activePointers = new Map<number, { x: number; y: number }>();
  let dragging = false;
  let dragStart: { x: number; y: number } | null = null;
  let panStart: PanOffset = { x: 0, y: 0 };
  let pinchActive = false;
  let pinchStartDistance = 0;
  let pinchStartZoom = DEFAULT_ZOOM;
  /** True once a gesture has involved 2+ pointers — suppresses the tap-to-open-popover behavior on eventual release. */
  let gestureHadPinch = false;

  function beginDragFrom(clientX: number, clientY: number): void {
    dragging = true;
    dragStart = { x: clientX, y: clientY };
    panStart = { ...panOffset };
  }

  /**
   * Tracks which content-bearing cell the pointer is currently over, for the
   * hover highlight drawn in draw() — a SEPARATE listener from onPointerMove
   * (which only runs its own body once an actual drag/pinch is in progress)
   * since this one needs to run on every idle hover, drag or not, and bail
   * out itself instead.
   */
  function onCanvasPointerMove(e: PointerEvent): void {
    if (dragging || pinchActive) return;
    const rect = canvas.getBoundingClientRect();
    const localX = e.clientX - rect.left;
    const localY = e.clientY - rect.top;
    const { displayWidth, displayHeight } = currentDisplaySize();
    const xu = displayWidth > 0 ? (localX - panOffset.x - displayWidth / 2) / displayWidth : 0;
    const yu = displayHeight > 0 ? (localY - panOffset.y) / displayHeight : 0;
    const cell = isoProjectedToGridCell(xu, yu);
    const poi = cell ? poiByCell.get(cellKey(cell.x, cell.y)) : null;
    const hasContent = poi && (poi.kind !== 'COLONY_PLOT' || !!poi.colonyId);
    const newHovered = hasContent && cell ? cell : null;
    const changed = hoveredCell?.x !== newHovered?.x || hoveredCell?.y !== newHovered?.y;
    if (changed) {
      hoveredCell = newHovered;
      canvas.classList.toggle('cursor-interactive', !!newHovered);
      scheduleRedraw();
    }
  }

  function onPointerDown(e: PointerEvent): void {
    canvas.setPointerCapture(e.pointerId);
    activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (activePointers.size >= 2) {
      pinchActive = true;
      gestureHadPinch = true;
      dragging = false;
      dragStart = null;
      const pts = Array.from(activePointers.values());
      pinchStartDistance = distanceBetween(pts[0], pts[1]);
      pinchStartZoom = zoom;
    } else {
      gestureHadPinch = false;
      beginDragFrom(e.clientX, e.clientY);
    }
  }

  function onPointerMove(e: PointerEvent): void {
    if (!activePointers.has(e.pointerId)) return;
    activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pinchActive && activePointers.size >= 2) {
      const pts = Array.from(activePointers.values());
      const dist = distanceBetween(pts[0], pts[1]);
      if (pinchStartDistance > 0) {
        const rect = canvas.getBoundingClientRect();
        const midClient = midpoint(pts[0], pts[1]);
        const anchor = { x: midClient.x - rect.left, y: midClient.y - rect.top };
        applyZoom(pinchStartZoom * (dist / pinchStartDistance), anchor);
      }
      return;
    }

    if (!dragging || !dragStart) return;
    const dx = e.clientX - dragStart.x;
    const dy = e.clientY - dragStart.y;
    const { displayWidth, displayHeight } = currentDisplaySize();
    panOffset = clampPanOffset(
      { x: panStart.x + dx, y: panStart.y + dy },
      wrap.clientWidth,
      wrap.clientHeight,
      displayWidth,
      displayHeight,
    );
    scheduleRedraw();
  }

  function endDrag(e: PointerEvent): void {
    const wasSingleDrag = dragging && !!dragStart && !gestureHadPinch;
    const dx = dragStart ? e.clientX - dragStart.x : 0;
    const dy = dragStart ? e.clientY - dragStart.y : 0;

    activePointers.delete(e.pointerId);

    if (activePointers.size < 2) {
      pinchActive = false;
      pinchStartDistance = 0;
    }

    if (activePointers.size === 1) {
      // One finger remains after a pinch — resume single-finger panning from
      // its current position (no jump), and remember this was a multi-touch
      // gesture so eventually lifting the last finger doesn't open a popover.
      const [remaining] = Array.from(activePointers.values());
      beginDragFrom(remaining.x, remaining.y);
      gestureHadPinch = true;
      return;
    }

    if (activePointers.size === 0) {
      dragging = false;
      dragStart = null;
      const wasTap = wasSingleDrag && Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD;
      gestureHadPinch = false;
      if (wasTap) {
        const rect = canvas.getBoundingClientRect();
        const localX = e.clientX - rect.left;
        const localY = e.clientY - rect.top;
        const { displayWidth, displayHeight } = currentDisplaySize();
        const xu = displayWidth > 0 ? (localX - panOffset.x - displayWidth / 2) / displayWidth : 0;
        const yu = displayHeight > 0 ? (localY - panOffset.y) / displayHeight : 0;
        const cell = isoProjectedToGridCell(xu, yu);
        if (cell) {
          openPopover(cell.x, cell.y, e.clientX, e.clientY);
        } else {
          closePopover();
        }
      }
    }
  }

  function onPointerUp(e: PointerEvent): void {
    endDrag(e);
  }

  function onPointerCancel(e: PointerEvent): void {
    activePointers.delete(e.pointerId);
    if (activePointers.size < 2) {
      pinchActive = false;
      pinchStartDistance = 0;
    }
    if (activePointers.size === 0) {
      dragging = false;
      dragStart = null;
      gestureHadPinch = false;
    }
  }

  function onWheel(e: WheelEvent): void {
    e.preventDefault();
    const rect = canvas.getBoundingClientRect();
    const anchor = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    const factor = Math.exp(-e.deltaY * WHEEL_ZOOM_SENSITIVITY);
    applyZoom(zoom * factor, anchor);
  }

  // ---- Minimap pointer wiring (click/drag jumps the main view) --------

  let minimapDragging = false;

  function minimapEventToLocal(e: PointerEvent): { x: number; y: number } {
    // offsetX/Y are in the canvas's OWN space, so the 3D lean app.css gives the minimap
    // cannot skew where a click lands (a screen-rect subtraction would). Falls back to
    // the rect for environments that do not report them.
    if (Number.isFinite(e.offsetX) && Number.isFinite(e.offsetY) && (e.offsetX !== 0 || e.offsetY !== 0 || e.clientX === 0)) {
      return { x: e.offsetX, y: e.offsetY };
    }
    const rect = minimapCanvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  /**
   * Re-centers the main view on the caller's own colony plot — a no-op if
   * the account has no colony yet or it isn't in the currently-loaded POI
   * set (e.g. still loading). Reuses computeMinimapJumpPan's math by feeding
   * it the colony's position in full-image pixel space directly (minimapWidth/
   * minimapHeight = the image's own dimensions makes minimapX/minimapY act as
   * that same pixel space) rather than routing through the minimap canvas's
   * on-screen size, which jumpToMinimapPoint needs but this doesn't.
   */
  function focusOnHomeColony(): void {
    const home = pois.find((p) => p.colonyId === myColonyId);
    if (!home) return;
    const imgPos = gridPositionToImagePixel(home.x + 0.5, home.y + 0.5);
    const { displayWidth, displayHeight } = currentDisplaySize();
    panOffset = computeMinimapJumpPan(
      imgPos.x,
      imgPos.y,
      MARS_IMAGE_WIDTH,
      MARS_IMAGE_HEIGHT,
      displayWidth,
      displayHeight,
      wrap.clientWidth,
      wrap.clientHeight,
    );
    scheduleRedraw();
  }
  focusHomeBtn.addEventListener('click', focusOnHomeColony);
  homeNoticeDismissBtn.addEventListener('click', onDismissHomeNotice);

  function jumpToMinimapPoint(minimapX: number, minimapY: number): void {
    const width = minimapCanvas.clientWidth;
    const height = minimapCanvas.clientHeight;
    if (width <= 0 || height <= 0) return;
    const { displayWidth, displayHeight } = currentDisplaySize();
    panOffset = computeMinimapJumpPan(minimapX, minimapY, width, height, displayWidth, displayHeight, wrap.clientWidth, wrap.clientHeight);
    scheduleRedraw();
  }

  function onMinimapPointerDown(e: PointerEvent): void {
    minimapDragging = true;
    minimapCanvas.setPointerCapture(e.pointerId);
    const { x, y } = minimapEventToLocal(e);
    jumpToMinimapPoint(x, y);
  }

  function onMinimapPointerMove(e: PointerEvent): void {
    if (!minimapDragging) return;
    const { x, y } = minimapEventToLocal(e);
    jumpToMinimapPoint(x, y);
  }

  function onMinimapPointerUp(): void {
    minimapDragging = false;
  }

  function onMinimapPointerCancel(): void {
    minimapDragging = false;
  }

  // ---- Data loading ----------------------------------------------------

  /**
   * Which world's map to show. A player's colony names its world, so that
   * wins. A colonyless account has no world of its own, and the existing
   * contract is that it still gets a browsable map — so it falls back to the
   * first joinable world, which is also the one the world picker would
   * default to (design/gdd/multi-world.md phase E).
   */
  async function resolveWorldId(): Promise<{ worldId: string | null; colonyId: string | null }> {
    const colonyResult = await Promise.allSettled([fetchMyColony()]);
    const colony = colonyResult[0].status === 'fulfilled' ? colonyResult[0].value : null;
    if (colony) return { worldId: colony.worldId, colonyId: colony.id };

    try {
      const joinable = await fetchJoinableWorlds();
      return { worldId: joinable[0]?.id ?? null, colonyId: null };
    } catch {
      return { worldId: null, colonyId: null };
    }
  }

  async function load(): Promise<void> {
    const { worldId, colonyId } = await resolveWorldId();
    myColonyId = colonyId;
    // Was a silent no-op for a colonyless account before this — now visibly inert instead.
    focusHomeBtn.disabled = myColonyId === null;

    if (!worldId) {
      const message = t('worldmap.noWorld');
      if (!disposed) statusEl.textContent = message;
      return;
    }

    try {
      pois = await fetchWorldMap(worldId);
    } catch (err) {
      const message = err instanceof Error ? err.message : t('worldmap.loadFailed');
      if (!disposed) statusEl.textContent = message;
      showToast(message, 'error');
      return;
    }
    rebuildIndex();
    cachedRevealed = revealedSectors(
      sectors.map((s) => {
        let claimed = 0;
        let total = 0;
        for (const poi of pois) {
          if (poi.kind !== 'COLONY_PLOT') continue;
          if (poi.x >= s.minX && poi.x <= s.maxX && poi.y >= s.minY && poi.y <= s.maxY) {
            total++;
            if (poi.colonyId) claimed++;
          }
        }
        return { claimed, total };
      }),
      SECTOR_COLS,
      SECTOR_ROWS,
    );
    // The Map tab is now the post-login landing tab (see engine/bootstrap.ts)
    // — open it centered on the player's own colony rather than the map's
    // generic middle, which resize() already set as a blank-screen fallback
    // before this data arrived. No-ops for a colonyless account (focusOnHomeColony
    // finds nothing in `pois` to center on).
    focusOnHomeColony();
    showHomeNoticeIfPending();

    if (disposed) return;
    statusEl.textContent = '';
    scheduleRedraw();
  }

  function onPointerLeave(): void {
    if (hoveredCell) {
      hoveredCell = null;
      canvas.classList.remove('cursor-interactive');
      scheduleRedraw();
    }
  }

  canvas.addEventListener('pointerdown', onPointerDown);
  canvas.addEventListener('pointermove', onPointerMove);
  canvas.addEventListener('pointermove', onCanvasPointerMove);
  canvas.addEventListener('pointerup', onPointerUp);
  canvas.addEventListener('pointercancel', onPointerCancel);
  canvas.addEventListener('pointerleave', onPointerLeave);
  canvas.addEventListener('wheel', onWheel, { passive: false });
  minimapCanvas.addEventListener('pointerdown', onMinimapPointerDown);
  minimapCanvas.addEventListener('pointermove', onMinimapPointerMove);
  minimapCanvas.addEventListener('pointerup', onMinimapPointerUp);
  minimapCanvas.addEventListener('pointercancel', onMinimapPointerCancel);
  window.addEventListener('resize', resize);
  window.addEventListener('orientationchange', resize);
  // The map's box can change without the window resizing — the shell restyles its header and
  // title strips per tab — and a canvas buffer left at the old size leaves an unpainted (and
  // therefore un-fogged) strip along the bottom edge. Follow the container itself.
  const wrapObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => resize()) : null;
  wrapObserver?.observe(wrap);

  document.addEventListener('visibilitychange', onCloudVisibilityChange);

  resize(); // initial sizing so the map isn't blank while data/image load
  loadMarsImage();
  // Clouds load after the map so they never compete with first paint.
  for (let i = 0; i < CLOUD_SPRITE_COUNT; i++) loadCloudImage(i);
  startCloudLoop();
  loadDuneImage();
  loadCrimsonDustImage();
  loadCraterImage(0, '/decorations/crater-1.webp');
  loadCraterImage(1, '/decorations/crater-2.webp');
  loadMountainImage(0, '/decorations/mountain-1.webp');
  loadMountainImage(1, '/decorations/mountain-2.webp');
  loadMountainImage(2, '/decorations/mountain-3.webp');
  loadMountainImage(3, '/decorations/mountain-4.webp');
  void load();
  // Barbarian fort icons themselves are loaded lazily (getBarbarianMapIcon,
  // called from draw()) — nothing to kick off here.

  return () => {
    disposed = true;
    stopCloudLoop();
    document.removeEventListener('visibilitychange', onCloudVisibilityChange);
    canvas.removeEventListener('pointerdown', onPointerDown);
    canvas.removeEventListener('pointermove', onPointerMove);
    canvas.removeEventListener('pointermove', onCanvasPointerMove);
    canvas.removeEventListener('pointerup', onPointerUp);
    canvas.removeEventListener('pointercancel', onPointerCancel);
    canvas.removeEventListener('pointerleave', onPointerLeave);
    canvas.removeEventListener('wheel', onWheel);
    focusHomeBtn.removeEventListener('click', focusOnHomeColony);
    homeNoticeDismissBtn.removeEventListener('click', onDismissHomeNotice);
    minimapCanvas.removeEventListener('pointerdown', onMinimapPointerDown);
    minimapCanvas.removeEventListener('pointermove', onMinimapPointerMove);
    minimapCanvas.removeEventListener('pointerup', onMinimapPointerUp);
    minimapCanvas.removeEventListener('pointercancel', onMinimapPointerCancel);
    stopBoostHud();
    wrapObserver?.disconnect();
    window.removeEventListener('resize', resize);
    window.removeEventListener('orientationchange', resize);
    closePopover();
  };
}

export const worldMapTab: GameTab = {
  id: 'worldmap',
  label: t('nav.map'),
  icon: 'map',
  mount: renderWorldMapScreen,
};
